import { useEffect, useRef, useCallback, useMemo } from 'react';

interface UseSwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Minimum swipe distance in pixels. If not provided, scales from 50px on mobile to 80px on large screens. */
  threshold?: number;
  enabled?: boolean;
}

/**
 * Calculate default threshold based on viewport width.
 * Larger screens require larger swipes to feel intentional.
 * Scales from 50px (mobile) to 80px (desktop), capped at 80px.
 */
const getDefaultThreshold = (): number => {
  if (typeof window === 'undefined') return 50;
  const width = window.innerWidth;

  // Scales linearly from 50px at 375px to 80px at ~1027px, then caps
  return Math.min(50 + (width - 375) * 0.046, 80);
};

/**
 * Custom hook for handling touch/swipe gestures using Pointer Events API.
 * Provides a consistent cross-browser experience for swipe navigation.
 *
 * @param options - Configuration options
 * @param options.onSwipeLeft - Callback when user swipes left (typically navigate to next)
 * @param options.onSwipeRight - Callback when user swipes right (typically navigate to previous)
 * @param options.threshold - Minimum swipe distance in pixels to trigger (default: scales 50-80px based on viewport)
 * @param options.enabled - Whether gesture detection is active (default: true)
 * @returns ref - Ref to attach to the swipeable element
 *
 * @example
 * ```tsx
 * const swipeRef = useSwipeGesture({
 *   onSwipeLeft: () => goToNext(),
 *   onSwipeRight: () => goToPrevious(),
 * });
 *
 * return <div ref={swipeRef}>Swipeable content</div>;
 * ```
 */
export const useSwipeGesture = <T extends HTMLElement>({
  onSwipeLeft,
  onSwipeRight,
  threshold,
  enabled = true,
}: UseSwipeGestureOptions) => {
  const ref = useRef<T>(null);

  // Calculate effective threshold - use provided value or scale based on viewport
  const effectiveThreshold = useMemo(() => threshold ?? getDefaultThreshold(), [threshold]);
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const activePointerId = useRef<number | null>(null);
  const maxPointersInGesture = useRef(0);
  const activePointerCount = useRef(0);

  const handlePointerDown = useCallback((e: PointerEvent) => {
    const element = ref.current;
    if (!element) return;

    activePointerCount.current++;
    maxPointersInGesture.current = Math.max(maxPointersInGesture.current, activePointerCount.current);

    // Capture pointer on the container element (not e.target which could be a child)
    // to ensure we receive pointerup even if finger moves outside element
    element.setPointerCapture(e.pointerId);
    activePointerId.current = e.pointerId;

    startX.current = e.clientX;
    startY.current = e.clientY;
  }, []);

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const element = ref.current;

      activePointerCount.current = Math.max(0, activePointerCount.current - 1);

      // Release pointer capture on the container element
      if (activePointerId.current !== null && element) {
        if (element.hasPointerCapture(activePointerId.current)) {
          element.releasePointerCapture(activePointerId.current);
        }
        activePointerId.current = null;
      }

      if (startX.current === null || startY.current === null) return;

      // Skip swipe detection if multiple pointers were active (e.g. pinch gesture)
      if (maxPointersInGesture.current > 1) {
        startX.current = null;
        startY.current = null;
        if (activePointerCount.current === 0) maxPointersInGesture.current = 0;
        return;
      }

      const deltaX = e.clientX - startX.current;
      const deltaY = e.clientY - startY.current;

      // Only trigger if horizontal movement exceeds vertical (prevent scroll conflicts)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > effectiveThreshold) {
        if (deltaX > 0 && onSwipeRight) {
          onSwipeRight(); // Swipe right = previous
        } else if (deltaX < 0 && onSwipeLeft) {
          onSwipeLeft(); // Swipe left = next
        }
      }

      startX.current = null;
      startY.current = null;
      maxPointersInGesture.current = 0;
    },
    [onSwipeLeft, onSwipeRight, effectiveThreshold],
  );

  // Reset state when gesture is interrupted (e.g., by system gestures on Windows hybrids,
  // palm rejection, or browser scroll taking over the touch)
  const handlePointerCancel = useCallback(() => {
    const element = ref.current;

    activePointerCount.current = Math.max(0, activePointerCount.current - 1);

    // Release pointer capture if held on the container element
    if (activePointerId.current !== null && element) {
      if (element.hasPointerCapture(activePointerId.current)) {
        element.releasePointerCapture(activePointerId.current);
      }
      activePointerId.current = null;
    }

    startX.current = null;
    startY.current = null;
    if (activePointerCount.current === 0) maxPointersInGesture.current = 0;
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    // Reset multi-pointer tracking so stale state from a previous
    // enable cycle (e.g. pinch that disabled swipes) doesn't corrupt detection
    activePointerCount.current = 0;
    maxPointersInGesture.current = 0;

    element.addEventListener('pointerdown', handlePointerDown);
    element.addEventListener('pointerup', handlePointerUp);
    element.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointerup', handlePointerUp);
      element.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [enabled, handlePointerDown, handlePointerUp, handlePointerCancel]);

  return ref;
};
